import { ArrowRight } from "lucide-react";
import { SlideFrame, SlideHeader, Reveal } from "@/components/presentation/slides/SlideFrame";
import { Card } from "@/components/primitives/Card";
import { MetricCard } from "@/components/primitives/MetricCard";
import { Callout } from "@/components/primitives/Callout";

/**
 * 09 · BusinessModelSlide
 *
 * Diagram of value flow + pricing tiers + unit economics. Built to stay
 * single-page even with three concepts: the diagram is the headline; pricing
 * and unit economics serve as supporting blocks beneath.
 */
const DEFAULT_FLOW = [
  { id: "n1", label: "Input",       body: "Where it begins."         },
  { id: "n2", label: "HAEVN",       body: "What we do with it.",     emphasis: true },
  { id: "n3", label: "Outcome",     body: "What the customer feels." },
];

const DEFAULT_PRICING = [
  { id: "p1", name: "Tier one",   price: "—", hint: "Entry"        },
  { id: "p2", name: "Tier two",   price: "—", hint: "Standard"     },
  { id: "p3", name: "Tier three", price: "—", hint: "Enterprise"   },
];

const DEFAULT_UNIT_ECON = [
  { id: "u1", label: "Gross margin", value: "—%"  },
  { id: "u2", label: "Payback",      value: "— mo" },
  { id: "u3", label: "LTV / CAC",    value: "—x"   },
];

const FlowNode = ({ node, testId }) => (
  <Card
    padding="default"
    testId={testId}
    className={
      node.emphasis
        ? "border-brand/30 bg-brand/[0.04]"
        : ""
    }
  >
    <div className="haevn-eyebrow mb-2">{node.label}</div>
    <div className="text-sm leading-relaxed text-muted-foreground">{node.body}</div>
  </Card>
);

export const BusinessModelSlide = ({
  eyebrow = "Business Model",
  title = "How value moves through the company.",
  narrative = "One sentence that names the central exchange. The diagram supplies the rest.",
  flow = DEFAULT_FLOW,
  pricing = DEFAULT_PRICING,
  unitEconomics = DEFAULT_UNIT_ECON,
  insight = "Replace with a sentence that connects the model to the moat.",
}) => {
  return (
    <SlideFrame testId="slide-business-model" width="wide">
      <SlideHeader eyebrow={eyebrow} title={title} lede={narrative} />

      <Reveal delay={2}>
        <div
          data-testid="business-flow"
          className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr]"
        >
          <FlowNode node={flow[0]} testId="business-flow-node-0" />
          <div className="hidden items-center justify-center text-muted-foreground sm:flex" aria-hidden="true">
            <ArrowRight className="h-4 w-4" />
          </div>
          <FlowNode node={flow[1]} testId="business-flow-node-1" />
          <div className="hidden items-center justify-center text-muted-foreground sm:flex" aria-hidden="true">
            <ArrowRight className="h-4 w-4" />
          </div>
          <FlowNode node={flow[2]} testId="business-flow-node-2" />
        </div>
      </Reveal>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Reveal delay={3}>
          <Card padding="lg" testId="business-pricing">
            <div className="haevn-eyebrow mb-4">Pricing</div>
            <ul className="flex flex-col divide-y divide-border">
              {pricing.map((p) => (
                <li
                  key={p.id}
                  data-testid={`business-pricing-${p.id}`}
                  className="flex items-baseline justify-between gap-6 py-3"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">{p.name}</span>
                    <span className="text-xs text-muted-foreground">{p.hint}</span>
                  </div>
                  <span className="font-mono text-base tabular-nums text-foreground">{p.price}</span>
                </li>
              ))}
            </ul>
          </Card>
        </Reveal>
        <Reveal delay={4}>
          <Card padding="lg" testId="business-unit-economics">
            <div className="haevn-eyebrow mb-4">Unit economics</div>
            <div className="grid grid-cols-3 gap-px overflow-hidden rounded-lg border border-border bg-border">
              {unitEconomics.map((u) => (
                <MetricCard
                  key={u.id}
                  label={u.label}
                  value={u.value}
                  className="rounded-none border-0"
                  testId={`business-unit-${u.id}`}
                />
              ))}
            </div>
          </Card>
        </Reveal>
      </div>

      <Reveal delay={5}>
        <Callout variant="note" title="What this means" testId="business-insight">
          {insight}
        </Callout>
      </Reveal>
    </SlideFrame>
  );
};

export default BusinessModelSlide;
